import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import { useMemo } from 'react';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { buildEventFormValues } from '../../../utils/buildEventPayload';

export interface PromoEntry {
    promo_id: string;
    promo_name: string;
    /**
     * Promo-level attributes from the event details response
     * (e.g. `{ mechanic: 'flat_fee_ecommerce' }`).
     */
    attributes?: Record<string, unknown>;
    /**
     * Convenience alias of `attributes.mechanic` for rule-engine consumers.
     * Prefer reading `attributes.mechanic` for new call sites.
     */
    promo_mechanic?: string;
}

interface DimensionAttributeValue {
    display_name: string;
    values: string[] | { value: string; dim_id: number }[];
}

export interface EventDataResponse {
    event_id: string;
    plan_id: string;
    event_name: string;
    start_date: string;
    end_date: string;
    display_acv: number | null;
    feature_acv: number | null;
    created_by: string | null;
    event_status: string;
    lowest_dim_attr_id: number[];
    promos: PromoEntry[];
    dimension_attributes: Record<string, DimensionAttributeValue>;
    attributes?: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

const fetchEventById = async (eventId: string, signal?: AbortSignal) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getEventById(eventId), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });
    return response.data as EventDataResponse;
};

const useGetEventData = (eventId: string | null) => {
    const { eventDetailsFields, entityAttributes, promoDetailsFields } = useTpoGenConfig();
    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: tpoGenKeys.eventDetails(eventId ?? ''),
        queryFn: ({ signal }) => fetchEventById(eventId as string, signal),
        enabled: !!eventId,
    });

    const formValues = useMemo(() => {
        if (!data) return null;
        const base = buildEventFormValues(entityAttributes, eventDetailsFields, data as unknown as Record<string, unknown>);
        const dimValues: Record<string, unknown> = {};
        for (const field of promoDetailsFields) {
            if (field.type !== 'dimSelect' || !field.name) continue;
            if (field.lowestAttribute) {
                dimValues[field.name] = data.lowest_dim_attr_id ?? [];
            } else {
                const attrData = data.dimension_attributes?.[field.attributeType];
                dimValues[field.name] = (attrData?.values as string[]) ?? [];
            }
        }
        return { ...base, ...dimValues };
    }, [data, eventDetailsFields, entityAttributes, promoDetailsFields]);

    const eventPromos = useMemo<PromoEntry[]>(() => {
        // Event details returns mechanic under `promos[].attributes.mechanic`
        // (not a top-level `promo_mechanic`). Keep attributes as returned and
        // mirror mechanic onto `promo_mechanic` for existing rule consumers.
        return (data?.promos ?? []).map((promo) => {
            const mechanicFromAttributes =
                typeof promo.attributes?.mechanic === 'string' ? promo.attributes.mechanic : undefined;
            return {
                ...promo,
                promo_mechanic: promo.promo_mechanic ?? mechanicFromAttributes,
            };
        });
    }, [data]);

    return {
        formValues,
        eventData: data ?? null,
        eventPromos,
        isPending: !!eventId && isPending,
        isFetching,
        isError,
    };
};

export default useGetEventData;
